import { clearCredentials, getAuthHeader } from './token-store';
import type {
  AgentJob,
  ApiKeySummary,
  AssetGroup,
  AuditEvent,
  Authorization,
  AuthorizationMethod,
  Broker,
  ConsentRecord,
  CreatedPlatformApiKey,
  CreateWorkspaceResult,
  Engagement,
  EngagementStatus,
  EndpointAgent,
  FindingDetailRecord,
  FindingRecord,
  GitHubAllowlistedRepo,
  GitHubExportOutcome,
  GitHubInstallation,
  GitHubRepoRef,
  GitHubScanFinding,
  GitHubScanRunResult,
  ImpersonateResult,
  InternalAgent,
  KillSwitchState,
  MemberSummary,
  Membership,
  MembershipStatus,
  ReportJson,
  RetestDiff,
  Role,
  Run,
  RunEvent,
  RunMetrics,
  ScanWindow,
  ScopeEvaluation,
  ScopePolicy,
  ScopePolicyRecord,
  ScopeTarget,
  ScopeZone,
  Target,
  TargetKind,
  Tenant,
  WorkspaceDetail,
  WorkspaceSummary,
  PlatformApiKeySummary,
} from './types';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail?: unknown,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: opts.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
      ...opts.headers,
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401) {
    clearCredentials();
  }

  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    let detail: unknown;
    try {
      const parsed = (await res.json()) as { error?: string; reason?: string; message?: string; detail?: unknown };
      code = parsed.error ?? code;
      detail = parsed.detail ?? parsed.reason ?? parsed.message;
    } catch {
      // non-JSON error body — keep the generic HTTP_<status> code
    }
    throw new ApiError(res.status, code, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function requestBytes(path: string): Promise<Blob> {
  const res = await fetch(`${BASE_URL}${path}`, { headers: { ...getAuthHeader() } });
  if (res.status === 401) clearCredentials();
  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    try {
      const parsed = (await res.json()) as { error?: string };
      code = parsed.error ?? code;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, code);
  }
  return res.blob();
}

// ---- Auth ----

export interface SignupInput {
  tenantName: string;
  tenantSlug: string;
  email: string;
  name: string;
  password: string;
}

export interface AuthResult {
  accessToken: string;
  tenantId: string;
  role: Role;
  userId: string;
}

export const authApi = {
  signup: (input: SignupInput) =>
    request<{ tenant: Tenant; user: { id: string; email: string; name: string }; role: Role; accessToken: string }>(
      '/v1/auth/signup',
      { method: 'POST', body: input },
    ),
  login: (input: { tenantSlug: string; email: string; password: string }) =>
    request<AuthResult>('/v1/auth/login', { method: 'POST', body: input }),
  ssoLogin: (input: { tenantSlug: string; email?: string }) => request<{ url: string; ssoState: string }>('/v1/auth/sso/login', { method: 'POST', body: input }),
  ssoCallback: (input: { code: string; state: string; ssoState: string }) =>
    request<AuthResult>('/v1/auth/sso/callback', { method: 'POST', body: input }),
  ssoConfig: () => request<{ ssoEnabled: boolean; providerName: string }>('/v1/auth/sso/config'),
  /** Pre-SSO existence check (informational only — the SSO callback's own membership check is
   * still the real gate) so the login page can skip a doomed Microsoft round trip. Never throws
   * on "not found" — always resolves `{ exists: boolean }` (200); only a malformed request, a
   * missing SSO config, or a rate limit produce a real ApiError here. */
  checkEmail: (input: { email: string; tenantSlug: string }) => request<{ exists: boolean }>('/v1/auth/check-email', { method: 'POST', body: input }),
};

export const apiKeysApi = {
  list: () => request<{ apiKeys: ApiKeySummary[] }>('/v1/api-keys'),
  create: (input: { name: string; role: Role }) =>
    request<{ apiKey: ApiKeySummary; rawKey: string }>('/v1/api-keys', { method: 'POST', body: input }),
  revoke: (id: string) => request<{ revoked: true }>(`/v1/api-keys/${id}`, { method: 'DELETE' }),
};

// ---- Tenant ----

export const tenantApi = {
  me: () => request<{ tenant: Tenant }>('/v1/tenants/me'),
  update: (input: { name: string }) => request<{ tenant: Tenant }>('/v1/tenants/me', { method: 'PATCH', body: input }),
  updateSso: (input: { autoProvisionRole: Role | null }) =>
    request<{ tenant: Tenant }>('/v1/tenants/me/sso', { method: 'PATCH', body: input }),
};

// ---- Users ----

export const usersApi = {
  list: () => request<{ users: MemberSummary[] }>('/v1/users'),
  invite: (input: { email: string; name: string; role: Role }) =>
    request<{ userId: string; email: string; role: Role }>('/v1/users/invite', { method: 'POST', body: input }),
  updateRole: (userId: string, role: Role) =>
    request<{ membership: Membership }>(`/v1/users/${userId}/role`, { method: 'PATCH', body: { role } }),
  updateStatus: (userId: string, status: MembershipStatus) =>
    request<{ membership: Membership }>(`/v1/users/${userId}/status`, { method: 'PATCH', body: { status } }),
  remove: (userId: string) => request<{ removed: true }>(`/v1/users/${userId}`, { method: 'DELETE' }),
  changePassword: (input: { currentPassword: string; newPassword: string }) =>
    request<{ changed: true }>('/v1/users/me/password', { method: 'PATCH', body: input }),
};

// ---- Asset groups + targets ----

export const assetGroupsApi = {
  list: () => request<{ assetGroups: AssetGroup[] }>('/v1/asset-groups'),
  get: (id: string) => request<{ assetGroup: AssetGroup }>(`/v1/asset-groups/${id}`),
  create: (input: { name: string; zone: AssetGroup['zone'] }) =>
    request<{ assetGroup: AssetGroup }>('/v1/asset-groups', { method: 'POST', body: input }),
  remove: (id: string) => request<{ removed: true }>(`/v1/asset-groups/${id}`, { method: 'DELETE' }),
  targets: (id: string) => request<{ targets: Target[] }>(`/v1/asset-groups/${id}/targets`),
  addTarget: (id: string, input: { kind: TargetKind; value: string }) =>
    request<{ target: Target }>(`/v1/asset-groups/${id}/targets`, { method: 'POST', body: input }),
};

export const targetsApi = {
  remove: (id: string) => request<{ removed: true }>(`/v1/targets/${id}`, { method: 'DELETE' }),
};

// ---- Engagements ----

export const engagementsApi = {
  list: () => request<{ engagements: Engagement[] }>('/v1/engagements'),
  get: (id: string) => request<{ engagement: Engagement }>(`/v1/engagements/${id}`),
  create: (input: { name: string; scanWindows?: ScanWindow[] }) =>
    request<{ engagement: Engagement }>('/v1/engagements', { method: 'POST', body: input }),
  updateStatus: (id: string, status: EngagementStatus) =>
    request<{ engagement: Engagement }>(`/v1/engagements/${id}/status`, { method: 'PATCH', body: { status } }),
  updateScanWindows: (id: string, scanWindows: ScanWindow[]) =>
    request<{ engagement: Engagement }>(`/v1/engagements/${id}/scan-windows`, { method: 'PATCH', body: { scanWindows } }),
};

// ---- Scope policies ----

export const scopePoliciesApi = {
  list: (engagementId: string) => request<{ scopePolicies: ScopePolicyRecord[] }>(`/v1/engagements/${engagementId}/scope-policies`),
  create: (engagementId: string, policy: ScopePolicy) =>
    request<{ scopePolicy: ScopePolicyRecord }>(`/v1/engagements/${engagementId}/scope-policies`, { method: 'POST', body: { policy } }),
  update: (id: string, policy: ScopePolicy) => request<{ scopePolicy: ScopePolicyRecord }>(`/v1/scope-policies/${id}`, { method: 'PATCH', body: { policy } }),
};

export const scopeCheckApi = {
  check: (input: { scopePolicy: ScopePolicy; target: ScopeTarget }) => request<ScopeEvaluation>('/v1/scope/check', { method: 'POST', body: input }),
};

// ---- Authorizations ----

export type SubmitProofInput =
  | { method: 'dns_txt'; target: string; token: string }
  | { method: 'file_token'; target: string; token: string }
  | { method: 'signed_document'; evidenceRef: string }
  | { method: 'email_domain'; evidenceRef: string }
  | { method: 'account_owner'; evidenceRef: string };

export const authorizationsApi = {
  list: (engagementId: string) => request<{ authorizations: Authorization[] }>(`/v1/engagements/${engagementId}/authorizations`),
  create: (
    engagementId: string,
    input: { method: AuthorizationMethod; notBefore: string; notAfter: string; authorizedBy: { name: string; email: string } },
  ) => request<{ authorization: Authorization }>(`/v1/engagements/${engagementId}/authorizations`, { method: 'POST', body: input }),
  submitProof: (id: string, input: SubmitProofInput) =>
    request<{ authorization: Authorization }>(`/v1/authorizations/${id}/submit-proof`, { method: 'POST', body: input }),
  activate: (id: string) => request<{ authorization: Authorization }>(`/v1/authorizations/${id}/activate`, { method: 'POST' }),
};

// ---- Brokers (docs/broker.md) ----
// The original "only path to internal targets" system — apps/api/src/routes/brokers.ts, untouched
// since internalAgentsApi/BUI-1015 below became the lighter-weight surface for the same underlying
// idea. Still a real, live backend (registration + heartbeat + revoke), just not this frontend's
// primary internal-testing flow — surfaced under Advanced for whoever still provisions one.

export const brokersApi = {
  list: () => request<{ brokers: Broker[] }>('/v1/brokers'),
  register: (input: { name: string; allowedProtocols: string[]; mtlsFingerprint: string }) =>
    request<{ broker: Broker }>('/v1/brokers', { method: 'POST', body: input }),
  revoke: (id: string) => request<{ broker: Broker }>(`/v1/brokers/${id}/revoke`, { method: 'POST' }),
};

// ---- Internal testing agents (BUI-1015) ----
// Replaces the frontend's use of the older `brokers` API — that backend system stays as-is,
// untouched, for now; this is a separate, newer surface for the same underlying idea.

export const internalAgentsApi = {
  list: () => request<{ agents: InternalAgent[] }>('/v1/internal/agents'),
  generateToken: (input: { label?: string } = {}) =>
    request<{ token: string; expiresAt: string }>('/v1/internal/tokens', { method: 'POST', body: input }),
  revoke: (id: string) => request<{ agent: InternalAgent } | undefined>(`/v1/internal/agents/${id}`, { method: 'DELETE' }),
  /**
   * Removes a revoked/disconnected agent from the workspace entirely (soft delete server-side —
   * see apps/api/src/routes/internal-agents.ts's `DELETE /v1/internal/agents/:id/remove`). A
   * distinct path from `revoke` above, which already owns `DELETE /:id`. 409s if the agent is
   * still connected.
   */
  remove: (id: string) => request<{ agent: InternalAgent } | undefined>(`/v1/internal/agents/${id}/remove`, { method: 'DELETE' }),
  /**
   * BUI-1015 follow-up ("Task 1B"): dispatches a job to an already-registered agent (e.g. Quick
   * Scan kicking off an internal-target scan) — `internalAgent:dispatch` tier, Operator+, see
   * apps/api/src/routes/internal-agents.ts. `type` mirrors apps/agent/src/scans/run-job.ts's
   * provisional `Job` union.
   */
  createJob: (
    agentId: string,
    input: { type: 'port-scan' | 'service-fingerprint' | 'config-exposure'; payload: Record<string, unknown>; runId?: string },
  ) => request<{ job: AgentJob }>(`/v1/internal/agents/${agentId}/jobs`, { method: 'POST', body: input }),
};

// ---- Endpoint testing agents (BUI-1016) ----
// Fully separate system from internalAgentsApi/BUI-1015 above — a workstation posture-checking
// agent runs directly on an employee's machine (no broker), see apps/api/src/routes/endpoint-agents.ts.

export const endpointAgentsApi = {
  list: () => request<{ agents: EndpointAgent[] }>('/v1/endpoint/agents'),
  generateToken: (input: { label?: string } = {}) =>
    request<{ token: string; expiresAt: string }>('/v1/endpoint/tokens', { method: 'POST', body: input }),
  revoke: (id: string) => request<{ agent: EndpointAgent } | undefined>(`/v1/endpoint/agents/${id}`, { method: 'DELETE' }),
  /** See internalAgentsApi.remove's docstring — same soft-delete pattern, mirrored here. */
  remove: (id: string) => request<{ agent: EndpointAgent } | undefined>(`/v1/endpoint/agents/${id}/remove`, { method: 'DELETE' }),
};

// ---- Agent downloads (Task 1A) ----
// Backs the download buttons on InternalTesting.tsx/EndpointTesting.tsx. The underlying
// GET /v1/agent/downloads route is deliberately public/unauthenticated (see
// apps/api/src/routes/agent-downloads.ts) -- request() attaches an auth header when a token
// happens to exist in this browser session, but that's harmless here since the endpoint itself
// ignores auth entirely. Either field can be `null` if the corresponding env var isn't configured
// yet; callers must treat that as "not available", not throw.

export const agentDownloadsApi = {
  get: () =>
    request<{ windows: string | null; windowsEndpoint: string | null; linux: string | null }>('/v1/agent/downloads'),
};

// ---- Kali / Burp job dispatch (Phase 4 Red Team scan profiles — BUI-1019/BUI-1020) ----

export interface DispatchedJobStatus {
  jobId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
}

export const kaliJobsApi = {
  dispatch: (input: {
    runId: string;
    tool: string;
    zone: 'external' | 'internal' | 'app';
    target: { host?: string; ip?: string; port?: number };
    /** BUI-1045 — required for zone:'internal': the connected internal agent this job should be
     * routed to, since Kali's own Azure Container Apps Job has no network path to an RFC1918
     * target. Ignored for external/app dispatches. */
    agentId?: string;
  }) => request<DispatchedJobStatus>('/v1/kali/jobs', { method: 'POST', body: input }),
};

export const burpJobsApi = {
  dispatch: (input: { runId: string; scanProfile: string; zone: 'external' | 'internal' | 'app'; urls: string[] }) =>
    request<DispatchedJobStatus>('/v1/burp/jobs', { method: 'POST', body: input }),
};

/** BUI-1033 — OWASP ZAP: the default/primary web scanner, no license/scan-profile choice (unlike Burp). */
export const zapJobsApi = {
  dispatch: (input: { runId: string; zone: 'external' | 'internal' | 'app'; urls: string[] }) =>
    request<DispatchedJobStatus>('/v1/zap/jobs', { method: 'POST', body: input }),
};

export interface ScanCapabilityStatus {
  available: boolean;
  reason?: string;
}

/** BUI-1033 — which web scanner is actually usable right now: Burp Suite Pro when its license is
 * genuinely configured, ZAP (the free default) otherwise. See apps/api/src/azure/scan-capabilities.ts. */
export interface WebScannerStatus extends ScanCapabilityStatus {
  tool: 'burp' | 'zap';
}

export interface ScanCapabilities {
  kali: ScanCapabilityStatus;
  burp: ScanCapabilityStatus;
  zap: ScanCapabilityStatus;
  webScanner: WebScannerStatus;
}

/** Public, unauthenticated (see apps/api/src/app.ts) — a platform-wide fact, not tenant data. */
export const scanCapabilitiesApi = {
  get: () => request<ScanCapabilities>('/v1/scan/capabilities'),
};

// ---- AI features (packages/ai) ----
// Every route below degrades to this shape rather than a non-2xx response — see
// apps/api/src/routes/ai.ts/findings.ts/runs.ts and packages/ai/src/ai-call.ts. `not_configured`
// means OPENROUTER_API_KEY isn't set at all; `generation_failed` means the model call, or parsing
// its response, failed. Callers check `'error' in result` the same way FindingDetail.tsx's
// existing exploitabilityNarrative already does for its own `available`/`text: null` cases.
export interface AiFeatureError {
  error: 'not_configured' | 'generation_failed';
}

// ---- Findings ----

export const findingsApi = {
  forEngagement: (engagementId: string) =>
    request<{ byZone: Record<ScopeZone, FindingRecord[]>; total: number }>(`/v1/engagements/${engagementId}/findings`),
  get: (id: string) => request<{ finding: FindingDetailRecord }>(`/v1/findings/${id}`),
  // Tenant-wide, source-agnostic list (BUI-1016) — the only way agent-sourced findings (no
  // engagementId) surface at all; see Findings.tsx's merge of this alongside forEngagement().
  listForTenant: (target?: string) =>
    request<{ findings: FindingRecord[] }>(`/v1/findings${target ? `?target=${encodeURIComponent(target)}` : ''}`),
  // BUI-1017 "Confirm finding" — Owner-only; dispatches a safe re-probe, see FindingDetail.tsx.
  confirm: (id: string) => request<{ status: 'queued'; findingId: string }>(`/v1/findings/${id}/confirm`, { method: 'POST' }),
  exportToGithub: (id: string, input: { owner: string; repo: string }) =>
    request<{ issue: { number: number; url: string; created: boolean } }>(`/v1/findings/${id}/export-github`, { method: 'POST', body: input }),
  bulkExportToGithub: (input: { findingIds: string[]; owner: string; repo: string }) =>
    request<{ results: GitHubExportOutcome[] }>('/v1/findings/export-github', { method: 'POST', body: input }),
  // Phase 4 — AI "what could an attacker do" narrative (packages/ai's generateExploitabilityNarrative).
  // `available: false` means AI isn't configured at all; `available: true, text: null` means it IS
  // configured but the response didn't clear the confidence threshold — FindingDetail.tsx renders
  // these two cases differently.
  exploitabilityNarrative: (id: string) =>
    request<{ available: boolean; text: string | null; confidence: number }>(`/v1/findings/${id}/exploitability-narrative`, { method: 'POST' }),
  // AI feature — actionable remediation steps (packages/ai's generateRemediationGuidance).
  remediationGuidance: (id: string) =>
    request<{ steps: string[]; confidence: number } | AiFeatureError>(`/v1/findings/${id}/remediation-guidance`, { method: 'POST' }),
  // AI feature — false-positive likelihood assessment (packages/ai's assessFalsePositiveLikelihood).
  falsePositiveAssessment: (id: string) =>
    request<{ likelihood: 'low' | 'medium' | 'high'; rationale: string; confidence: number } | AiFeatureError>(
      `/v1/findings/${id}/false-positive-assessment`,
      { method: 'POST' },
    ),
  // AI feature — Safe Exploitation Proof (packages/ai's generateExploitationProof). Unlike every
  // other AI feature call above, this one can reject with a REAL 403 (ApiError, code
  // AUTHORIZATION_REQUIRED) when the finding's target has no active authorization/scope match —
  // see friendlyErrorMessage's AUTHORIZATION_REQUIRED case. `{error: ...}` (200) still covers the
  // "authorized but AI unavailable/failed" cases, same as every other AI feature.
  exploitationProof: (id: string) =>
    request<
      | { isPossible: boolean; proofType: 'safe-probe' | 'manual-verification' | 'not-applicable'; steps: string[]; expectedOutput: string; safetyNotes: string; confidence: number }
      | AiFeatureError
    >(`/v1/findings/${id}/exploitation-proof`, { method: 'POST' }),
};

// ---- Metasploit auxiliary-module confirmation (CLAUDE.md "Metasploit auxiliary-module
// exception") — internal-agent findings only; distinct from findingsApi.exploitationProof
// (external findings' AI-description-only flow) above. Every route here can reject with a real
// HTTP error (400/403 — ApiError), not just the `{error: ...}` (200) shape the other AI features
// use, since eligibility/authorization here gates a REAL dispatch, not just a generation call. ----

export interface ExploitModulePreview {
  module: string;
  options: Record<string, string>;
  mode: 'check' | 'auxiliary';
  expectedOutput: string;
  riskLevel: 'low' | 'medium' | 'high';
  explanation: string;
  confidence: number;
  agentId: string;
  agentHostname: string;
}

export interface ExploitationAuthorization {
  id: string;
  tenantId: string;
  findingId: string;
  runId: string | null;
  target: string;
  agentId: string;
  authorizedByName: string;
  authorizedByTitle: string;
  authorizedByUserId: string;
  metasploitModule: string;
  metasploitOptions: Record<string, string>;
  mode: 'check' | 'auxiliary';
  status: 'pending' | 'running' | 'completed' | 'failed';
  result: { rawOutput?: string; error?: string };
  exploitable: boolean | null;
  riskLevel: 'low' | 'medium' | 'high';
  createdAt: string;
  completedAt: string | null;
}

export const exploitationApi = {
  preview: (findingId: string) =>
    request<{ module: null; message: string } | ExploitModulePreview>(`/v1/findings/${findingId}/exploit/preview`, { method: 'POST' }),
  authorize: (
    findingId: string,
    input: { authorizedByName: string; authorizedByTitle: string; confirmationPhrase: string; acceptRisk: true; agentId: string },
  ) => request<{ authorizationId: string; status: string }>(`/v1/findings/${findingId}/exploit/authorize`, { method: 'POST', body: input }),
  status: (findingId: string, authorizationId: string) =>
    request<{ authorization: ExploitationAuthorization }>(`/v1/findings/${findingId}/exploit/status/${authorizationId}`),
};

// ---- GitHub Enterprise integration (BUI-972) ----
// Connection happens via the GitHub App install flow (a redirect + callback, not a token paste —
// see apps/api/src/routes/github.ts's install/start + install/callback and GitHubCallback.tsx),
// so there is no "submit a PAT" call here; nothing GitHub-credential-shaped is ever stored or
// returned by this API, matching the never-echo-a-secret posture apiKeysApi already has.

export const githubApi = {
  installation: () => request<{ installation: GitHubInstallation | null }>('/v1/integrations/github/installation'),
  installStart: () => request<{ url: string; installState: string }>('/v1/integrations/github/install/start', { method: 'POST' }),
  installCallback: (input: { installationId: string; code: string; state: string; installState: string }) =>
    request<{ installation: GitHubInstallation }>('/v1/integrations/github/install/callback', { method: 'POST', body: input }),
  disconnect: () => request<{ removed: true }>('/v1/integrations/github/installation', { method: 'DELETE' }),
  discoveredRepos: () => request<{ repos: GitHubRepoRef[] }>('/v1/integrations/github/repos/discovered'),
  allowlist: () => request<{ repos: GitHubAllowlistedRepo[] }>('/v1/integrations/github/repos/allowlist'),
  addToAllowlist: (input: { owner: string; repo: string }) =>
    request<{ repo: GitHubAllowlistedRepo }>('/v1/integrations/github/repos/allowlist', { method: 'POST', body: input }),
  removeFromAllowlist: (id: string) => request<{ removed: true }>(`/v1/integrations/github/repos/allowlist/${id}`, { method: 'DELETE' }),
};

// ---- GitHub Code Scanning (PAT-based, distinct from the App-install flow above) ----

export const githubScanApi = {
  run: (input: { repoUrl: string; pat: string }) => request<GitHubScanRunResult>('/v1/github-scan/run', { method: 'POST', body: input }),
  export: (input: { findings: GitHubScanFinding[]; engagementId: string }) =>
    request<{ findings: FindingRecord[] }>('/v1/github-scan/export', { method: 'POST', body: input }),
};

// ---- BUI Super-Admin Console ----

export const adminApi = {
  listWorkspaces: () => request<{ workspaces: WorkspaceSummary[] }>('/v1/admin/workspaces'),
  createWorkspace: (input: { name: string; slug: string; ownerEmail: string; ownerName: string }) =>
    request<CreateWorkspaceResult>('/v1/admin/workspaces', { method: 'POST', body: input }),
  getWorkspace: (id: string) => request<WorkspaceDetail>(`/v1/admin/workspaces/${id}`),
  impersonate: (id: string) => request<ImpersonateResult>(`/v1/admin/workspaces/${id}/impersonate`, { method: 'POST' }),
  provisionUser: (id: string, input: { email: string; name: string; role: Role }) =>
    request<{ userId: string; email: string; role: Role; temporaryPassword: string }>(`/v1/admin/workspaces/${id}/users`, {
      method: 'POST',
      body: input,
    }),
};

// ---- Platform API keys (BUI Super-Admin Console, /admin/platform-keys) — generic B2B partner
// integration credentials (docs/external-api). Every route here is `requireBuiStaff`-gated, same
// posture as adminApi above. ----

export const platformKeysApi = {
  list: () => request<{ keys: PlatformApiKeySummary[] }>('/v1/admin/platform-keys'),
  create: (input: {
    name: string;
    tenantId: string;
    scopes: string[];
    allowedIps?: string[];
    rateLimitPerHour?: number;
    webhookUrl?: string;
  }) => request<CreatedPlatformApiKey>('/v1/admin/platform-keys', { method: 'POST', body: input }),
  update: (
    id: string,
    patch: {
      name?: string;
      webhookUrl?: string | null;
      webhookSecret?: string | null;
      allowedIps?: string[];
      rateLimitPerHour?: number;
      isActive?: boolean;
    },
  ) => request<{ key: PlatformApiKeySummary }>(`/v1/admin/platform-keys/${id}`, { method: 'PATCH', body: patch }),
  revoke: (id: string) => request<{ key: PlatformApiKeySummary }>(`/v1/admin/platform-keys/${id}`, { method: 'DELETE' }),
};

// ---- Runs ----

export const runsApi = {
  // `profile` mirrors packages/shared's `runProfileSchema` — kept as `string` here (not a shared
  // import) since this bundle deliberately does not depend on @defendiq/shared, same reasoning as
  // QuickScan.tsx's own hand-maintained `ScanProfile` union.
  start: (engagementId: string, profile?: string) =>
    request<{ run: Run }>(`/v1/engagements/${engagementId}/runs`, { method: 'POST', body: profile ? { profile } : undefined }),
  list: (engagementId: string) => request<{ runs: Run[] }>(`/v1/engagements/${engagementId}/runs`),
  get: (id: string) => request<{ run: Run }>(`/v1/runs/${id}`),
  timeline: (id: string) => request<{ events: RunEvent[] }>(`/v1/runs/${id}/timeline`),
  metrics: (id: string) => request<RunMetrics>(`/v1/runs/${id}/metrics`),
  pause: (id: string) => request<{ run: Run }>(`/v1/runs/${id}/pause`, { method: 'POST' }),
  resume: (id: string) => request<{ run: Run }>(`/v1/runs/${id}/resume`, { method: 'POST' }),
  cancel: (id: string) => request<{ run: Run }>(`/v1/runs/${id}/cancel`, { method: 'POST' }),
  retest: (id: string) => request<{ run: Run }>(`/v1/runs/${id}/retest`, { method: 'POST' }),
  diff: (id: string) => request<{ diff: RetestDiff }>(`/v1/runs/${id}/diff`),
  // AI feature — plain-English executive summary (packages/ai's generateExecutiveSummary).
  executiveSummary: (id: string) => request<{ summary: string } | AiFeatureError>(`/v1/runs/${id}/executive-summary`, { method: 'POST' }),
  // AI feature — risk-based ranking of this run's findings (packages/ai's generateRiskPrioritisation).
  riskPrioritisation: (id: string) =>
    request<{ ranked: Array<{ id: string; rank: number; rationale: string }>; confidence: number } | AiFeatureError>(
      `/v1/runs/${id}/risk-prioritisation`,
      { method: 'POST' },
    ),
  // AI feature — attack-chain narrative across this run's findings (packages/ai's generateAttackPathNarrative).
  attackPathNarrative: (id: string) =>
    request<{ narrative: string } | AiFeatureError>(`/v1/runs/${id}/attack-path-narrative`, { method: 'POST' }),
  // AI feature — original-vs-retest comparison (packages/ai's generateRetestComparison). 400
  // NOT_A_RETEST if this run has no retestOfRunId — callers only offer this for a retest run.
  retestComparison: (id: string) =>
    request<{ summary: string; confidence: number } | AiFeatureError>(`/v1/runs/${id}/retest-comparison`, { method: 'POST' }),
};

// ---- AI (standalone, not tied to an existing finding/run) ----

export const aiApi = {
  // Public, unauthenticated (see apps/api/src/routes/ai.ts) — same posture as scanCapabilitiesApi
  // above: whether OPENROUTER_API_KEY is configured is one env-driven platform-wide fact.
  capabilities: () => request<{ enabled: boolean }>('/v1/ai/capabilities'),
  // AI feature — recommended scan profile for a target (packages/ai's generateScopeAdvice).
  scopeAdvice: (input: { targetUrl: string; engagementId?: string }) =>
    request<{ recommendedProfile: string; rationale: string; confidence: number } | AiFeatureError>('/v1/ai/scope-advice', {
      method: 'POST',
      body: input,
    }),
  // AI feature — attack-chain narrative not scoped to any single run (packages/ai's
  // generateAttackPathNarrative). Standalone counterpart to runsApi.attackPathNarrative, for
  // callers with no single run to scope the call to — see AttackPaths.tsx, which aggregates
  // findings (including agent-sourced ones with no runId at all) across every engagement/run.
  attackPathNarrative: (input: {
    findings: Array<{ title: string; severity: string; description: string; affectedAsset: string }>;
    targetName: string;
  }) =>
    request<{ narrative: string } | AiFeatureError>('/v1/ai/attack-path-narrative', {
      method: 'POST',
      body: input,
    }),
};

// ---- Reports ----

export const reportsApi = {
  // Always requests the BUI-branded PDF variant alongside the plain one — see
  // apps/api/src/routes/reports.ts's `?branded=true` (opt-in server-side, always-on from here).
  generate: (runId: string) =>
    request<{ report: ReportJson; jsonKey: string; pdfKey: string; brandedPdfKey?: string }>(
      `/v1/runs/${runId}/reports?branded=true`,
      { method: 'POST' },
    ),
  status: (runId: string) =>
    request<{ available: { json: boolean; pdf: boolean; pdfBranded: boolean }; jsonKey: string; pdfKey: string; brandedPdfKey?: string }>(
      `/v1/runs/${runId}/reports`,
    ),
  download: (runId: string, format: 'json' | 'pdf' | 'pdf-branded') => requestBytes(`/v1/runs/${runId}/reports/${format}`),
  listForEngagement: (engagementId: string) => request<{ keys: string[] }>(`/v1/engagements/${engagementId}/reports`),
};

// ---- Audit log ----

export const auditApi = {
  list: () => request<{ events: AuditEvent[] }>('/v1/audit'),
};

// ---- Consent (BUI-989) ----

export interface CreateConsentInput {
  checkboxes: {
    authorizedToTest: boolean;
    termsAccepted: boolean;
    liabilityAcknowledged: boolean;
  };
  documentBase64?: string;
}

export const consentApi = {
  create: (engagementId: string, input: CreateConsentInput) =>
    request<{ consentRecord: ConsentRecord }>(`/v1/engagements/${engagementId}/consent`, { method: 'POST', body: input }),
  get: (engagementId: string) => request<{ consentRecord: ConsentRecord | null }>(`/v1/engagements/${engagementId}/consent`),
};

// ---- Kill switch (BUI-960) ----
// Tenant-scoped only — see apps/api/src/routes/kill-switch.ts for why platform scope isn't exposed here.

export const killSwitchApi = {
  get: () => request<KillSwitchState>('/v1/kill-switch'),
  enable: (reason?: string) => request<KillSwitchState>('/v1/kill-switch/enable', { method: 'POST', body: reason ? { reason } : undefined }),
  disable: (reason?: string) => request<KillSwitchState>('/v1/kill-switch/disable', { method: 'POST', body: reason ? { reason } : undefined }),
};
